import {
  WebArError,
  parseWebArLaunchPayload,
  type LaunchValidationContext,
  type WebArLaunchPayload
} from "./contracts.js";

export interface LaunchFragmentPort {
  readHash(): string;
  clearHash(): void;
}

export interface LaunchReplayPort {
  claim(launchGrantId: string): boolean;
}

export class MemoryLaunchReplayGuard implements LaunchReplayPort {
  readonly #claimed = new Set<string>();

  claim(launchGrantId: string): boolean {
    if (this.#claimed.has(launchGrantId)) return false;
    this.#claimed.add(launchGrantId);
    return true;
  }
}

export interface SignedLaunchResolverOptions {
  readonly endpoint: string;
  readonly fragment: LaunchFragmentPort;
  readonly replay: LaunchReplayPort;
  readonly fetch: typeof fetch;
  readonly validation: () => LaunchValidationContext;
}

export interface SignedLaunchResolver {
  resolve(signal: AbortSignal): Promise<WebArLaunchPayload>;
}

const TOKEN_PATTERN = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){0,2}$/u;

export function readOpaqueLaunchToken(hash: string): string {
  const value = hash.startsWith("#") ? hash.slice(1) : hash;
  const parameters = new URLSearchParams(value);
  const token = parameters.get("launch");
  if (
    parameters.size !== 1 ||
    token === null ||
    token.length < 32 ||
    token.length > 4096 ||
    !TOKEN_PATTERN.test(token)
  ) {
    throw new WebArError("launch_missing", "A valid AR launch is required.", false);
  }
  return token;
}

function parseEndpoint(value: string): string {
  let endpoint: URL;
  try {
    endpoint = new URL(value);
  } catch (cause) {
    throw new WebArError("launch_rejected", "The launch endpoint is invalid.", false, { cause });
  }
  if (
    endpoint.protocol !== "https:" ||
    endpoint.username !== "" ||
    endpoint.password !== "" ||
    endpoint.hash !== "" ||
    endpoint.search !== ""
  ) {
    throw new WebArError("launch_rejected", "The launch endpoint is unsafe.", false);
  }
  return endpoint.href;
}

function parseEnvelope(value: unknown): unknown {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new WebArError("launch_rejected", "The launch response is invalid.", false);
  }
  const record = value as Record<string, unknown>;
  if (Object.keys(record).length !== 1 || !("data" in record)) {
    throw new WebArError("launch_rejected", "The launch response is invalid.", false);
  }
  return record.data;
}

export function createSignedLaunchResolver(
  options: SignedLaunchResolverOptions
): SignedLaunchResolver {
  const endpoint = parseEndpoint(options.endpoint);
  let inFlight: Promise<WebArLaunchPayload> | undefined;
  let retainedToken: string | undefined;

  return {
    resolve(signal) {
      if (inFlight) return inFlight;
      inFlight = (async () => {
        if (!retainedToken) {
          try {
            retainedToken = readOpaqueLaunchToken(options.fragment.readHash());
          } finally {
            options.fragment.clearHash();
          }
        }
        const token = retainedToken;

        let response: Response;
        try {
          response = await options.fetch(endpoint, {
            method: "POST",
            headers: {
              accept: "application/json",
              authorization: `Bearer ${token}`
            },
            credentials: "omit",
            redirect: "error",
            cache: "no-store",
            referrerPolicy: "no-referrer",
            signal
          });
        } catch (cause) {
          if (signal.aborted) {
            throw new WebArError("cancelled", "The AR launch was cancelled.", true, { cause });
          }
          throw new WebArError("launch_rejected", "The AR launch could not be verified.", true, {
            cause
          });
        }

        if (response.status === 409) {
          throw new WebArError("launch_replayed", "This AR launch has already been used.", false);
        }
        if (!response.ok) {
          throw new WebArError("launch_rejected", "The AR launch could not be verified.", true);
        }
        const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
        const contentLength = Number(response.headers.get("content-length") ?? "0");
        if (
          !contentType.startsWith("application/json") ||
          (Number.isFinite(contentLength) && contentLength > 32 * 1024)
        ) {
          throw new WebArError("launch_rejected", "The launch response is invalid.", false);
        }

        let json: unknown;
        try {
          json = await response.json();
        } catch (cause) {
          throw new WebArError("launch_rejected", "The launch response is invalid.", false, {
            cause
          });
        }
        const payload = parseWebArLaunchPayload(parseEnvelope(json), options.validation());
        if (!options.replay.claim(payload.launchGrantId)) {
          throw new WebArError("launch_replayed", "This AR launch has already been used.", false);
        }
        return payload;
      })().catch((error: unknown) => {
        inFlight = undefined;
        throw error;
      });
      return inFlight;
    }
  };
}

export function createBrowserLaunchFragmentPort(
  location: Pick<Location, "hash" | "pathname" | "search">,
  history: Pick<History, "replaceState">
): LaunchFragmentPort {
  return {
    readHash: () => location.hash,
    clearHash: () => history.replaceState(null, "", `${location.pathname}${location.search}`)
  };
}
